import api from '../lib/axios';
import type {
  LoginCredentials,
  AuthResponse,
  User,
  Meeting,
  MeetingFilters,
  PaginatedResponse,
  Contact,
  Product,
} from '../types';

// Auth API
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/auth/profile');
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// Meetings API
export const meetingsApi = {
  // Get meetings with filters
  getMeetings: async (filters?: MeetingFilters): Promise<PaginatedResponse<Meeting>> => {
    const response = await api.get<PaginatedResponse<Meeting>>('/meetings', {
      params: filters,
    });
    return response.data;
  },

  // Get meetings for today
  getTodayMeetings: async (): Promise<Meeting[]> => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const response = await api.get<Meeting[] | PaginatedResponse<Meeting>>('/meetings', {
        params: {
          startDate: today.toISOString(),
          endDate: tomorrow.toISOString(),
          limit: 100,
        },
      });

      // Backend returns array directly when using date range
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data?.data || [];
    } catch (error) {
      console.error('Error fetching today meetings:', error);
      return [];
    }
  },

  // Get upcoming meetings (next 7 days, excluding today)
  getUpcomingMeetings: async (): Promise<Meeting[]> => {
    try {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const nextWeek = new Date(tomorrow);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const response = await api.get<Meeting[] | PaginatedResponse<Meeting>>('/meetings', {
        params: {
          startDate: tomorrow.toISOString(),
          endDate: nextWeek.toISOString(),
          limit: 100,
        },
      });

      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data?.data || [];
    } catch (error) {
      console.error('Error fetching upcoming meetings:', error);
      return [];
    }
  },

  // Get meetings for a specific date range
  getMeetingsByDateRange: async (startDate: Date, endDate: Date): Promise<Meeting[]> => {
    try {
      const response = await api.get<Meeting[] | PaginatedResponse<Meeting>>('/meetings', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 1000,
        },
      });
      // Backend returns array directly when using date range, or paginated otherwise
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data?.data || [];
    } catch (error) {
      console.error('Error fetching meetings by date range:', error);
      return [];
    }
  },

  // Get single meeting by ID
  getMeeting: async (id: string): Promise<Meeting> => {
    const response = await api.get<Meeting>(`/meetings/${id}`);
    return response.data;
  },

  // Update meeting status
  updateMeetingStatus: async (
    id: string,
    status: Meeting['status']
  ): Promise<Meeting> => {
    const response = await api.patch<Meeting>(`/meetings/${id}`, { status });
    return response.data;
  },

  // Add notes to meeting
  addMeetingNotes: async (id: string, notes: string): Promise<Meeting> => {
    const response = await api.patch<Meeting>(`/meetings/${id}`, { notes });
    return response.data;
  },

  // Confirm meeting
  confirmMeeting: async (id: string): Promise<Meeting> => {
    const response = await api.patch<Meeting>(`/meetings/${id}/confirm`);
    return response.data;
  },

  // Cancel meeting
  cancelMeeting: async (id: string, reason?: string): Promise<Meeting> => {
    const response = await api.patch<Meeting>(`/meetings/${id}/cancel`, { reason });
    return response.data;
  },

  // Complete meeting
  completeMeeting: async (id: string): Promise<Meeting> => {
    const response = await api.patch<Meeting>(`/meetings/${id}`, { status: 'completed' });
    return response.data;
  },

  // Create meeting
  createMeeting: async (data: {
    contactId: string;
    productId: string;
    startTime: string; // ISO date string
    duration: number;
    notes?: string;
  }): Promise<Meeting> => {
    const response = await api.post<Meeting>('/meetings', data);
    return response.data;
  },
};

// Contacts API
export const contactsApi = {
  getContacts: async (search?: string): Promise<Contact[]> => {
    try {
      const response = await api.get<Contact[]>('/contacts', {
        params: search ? { search } : {},
      });
      // Garantir que sempre retorna um array
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      // Se for um objeto paginado, retornar data.data
      if (data && typeof data === 'object' && 'data' in data) {
        return (data as any).data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return [];
    }
  },

  getContact: async (id: string): Promise<Contact> => {
    const response = await api.get<Contact>(`/contacts/${id}`);
    return response.data;
  },

  getContactMeetings: async (contactId: string): Promise<Meeting[]> => {
    try {
      const response = await api.get<Meeting[] | PaginatedResponse<Meeting>>(
        `/meetings`,
        {
          params: { contactId, limit: 100 },
        }
      );
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data?.data || [];
    } catch (error) {
      console.error('Error fetching contact meetings:', error);
      return [];
    }
  },

  createContact: async (data: {
    name: string;
    phone: string;
    email?: string;
  }): Promise<Contact> => {
    const response = await api.post<Contact>('/contacts', data);
    return response.data;
  },
};

// Products API
export const productsApi = {
  getProducts: async (): Promise<Product[]> => {
    try {
      const response = await api.get<Product[]>('/products');
      // Garantir que sempre retorna um array
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      // Se for um objeto paginado, retornar data.data
      if (data && typeof data === 'object' && 'data' in data) {
        return (data as any).data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  },

  getProduct: async (id: string): Promise<Product> => {
    const response = await api.get<Product>(`/products/${id}`);
    return response.data;
  },
};

// Users API (for admin viewing other operators)
export const usersApi = {
  getUsers: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  getUser: async (id: string): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },
};
